import React, { useEffect } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Chip,
  ChipProps,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getCountry } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  createPremiumSession,
  getPremiumProductPrice,
  getStripeState,
} from '../../../../store/StripeSlice';
import { getCurrencySign } from '../../Diagnostic';
import { createAnalytics } from '../../../../store/AnalyticsSlice';
import BasicLoader from '../../../../components/Loaders/BasicLoader';

const outerBoxProps: BoxProps['sx'] = {
  display: 'inline-block',
  alignItems: 'center',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  padding: '2px',
  background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
};

const innerBoxProps: BoxProps['sx'] = {
  display: 'inline-block',
  backgroundColor: '#1E293B',
  color: 'white',
  fontSize: '15px',
  padding: '1px 10px',
  borderRadius: '20px',
  textAlign: 'center',
};

const stackStyles: SxProps = {
  marginTop: { xs: '10px', sm: '10px', md: '80px', lg: '80px' },
  alignItems: 'center',
  justifyContent: 'center',
};

const yearButtonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  sx: {
    background: 'linear-gradient(90deg, #01B8AF 0%, #00615D 100%)',
    color: 'white',
    width: { xs: '100px', sm: '100px', md: '210px', lg: '210px' },
    padding: '5px',
    height: '45px',
    fontSize: '15px',
    marginBottom: '8px',
    pointerEvents: 'auto',
    textTransform: 'none',
    fontWeight: 'bold',
    '&:hover': {
      background: 'linear-gradient(90deg, #02D1C7 0%, #007D78 100%)',
    },
  },
};

const quaterButtonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  sx: {
    background: '#FFFFFF',
    color: '#1E293B',
    width: { xs: '100px', sm: '100px', md: '160px', lg: '160px' },
    padding: '5px',
    height: '35px',
    fontSize: '15px',
    marginBottom: '8px',
    pointerEvents: 'auto',
    textTransform: 'none',
    fontWeight: 'bold',
    '&:hover': {
      background: 'linear-gradient(90deg, #f2f2f2 0%, #bfbfbf 100%)',
    },
  },
};

const chipProps: ChipProps = {
  size: 'small',
  sx: {
    height: '33px',
    fontSize: '14px',
    borderRadius: '4px',
    backgroundColor: '#B76E00',
    color: '#FFFFFF',
    position: 'absolute',
    top: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontWeight: 600,
    padding: '10px',
    zIndex: 1,
  },
};

const PremiumView: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // State

  // Store
  const authState = useAppSelector(getAuthState);
  const stripeState = useAppSelector(getStripeState);

  const { user } = authState;
  const { premiumPricesList, status } = stripeState;

  const monthlyPrice = premiumPricesList.length
    ? premiumPricesList[0]
    : undefined;

  // const quarterlyPrice = premiumPricesList.length
  //   ? premiumPricesList[1]
  //   : undefined;

  const yearlyPrice = premiumPricesList.length
    ? premiumPricesList[2]
    : undefined;

  const yearlyTotal = (monthlyPrice?.unitAmount || 1) * 12;
  const yearlyOffRate =
    Math.round(
      (((yearlyTotal - (yearlyPrice?.unitAmount || 1)) / yearlyTotal) * 100) / 5
    ) * 5;

  const currency = getCurrencySign(monthlyPrice?.currency || '');
  const country = getCountry();
  // const isDiscountEligible = checkDiscountEligibility();

  useEffect(() => {
    if (user) {
      dispatch(getPremiumProductPrice({ userId: user._id, country }));
    }
  }, []);

  const handleButtonClick = (priceId: string) => {
    if (user) {
      dispatch(
        createAnalytics({
          type: 'LP - Premium Popup',
          data: {
            selectedRole: user?.role.name,
            fullName: `${user.firstname} ${user.lastname || ''}`,
            phoneNumber: user.phoneNumber,
            country: user.country,
            source: 'Content',
            url: location.pathname.replace('/learning-portal/dashboard/', ''),
          },
          userId: user?._id,
        })
      );
      dispatch(
        createPremiumSession({
          userId: user._id,
          priceId,
          returnUrl: location.pathname.replace('learning-portal/', ''),
        })
      );
    }
  };

  return (
    <Stack sx={{ ...stackStyles }}>
      <img
        src={`${process.env.PUBLIC_URL}/images/leadGenModal/crown.png`}
        width="65px"
        height="65px"
        alt="crown"
      />
      <Typography fontSize={belowMd ? '18px' : '30px'} fontWeight="bold">
        Unlock your Full Learning Potential !
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          marginTop: '30px',
          justifyContent: 'center',
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/images/leadGenModal/plan-details.png`}
          width={belowMd ? '200px' : '280px'}
          height={belowMd ? '200px' : '280px'}
          alt="crown"
        />
        {status === 'loading' && premiumPricesList.length === 0 ? (
          <div style={{ padding: '30px' }}>
            <BasicLoader />
          </div>
        ) : (
          <>
            {yearlyPrice && (
              <Box
                sx={
                  yearlyOffRate
                    ? { position: 'relative', display: 'inline-block' }
                    : {}
                }
              >
                {yearlyOffRate && (
                  <Chip {...chipProps} label={`Save${yearlyOffRate}% off`} />
                )}
                <Box sx={outerBoxProps}>
                  <Box sx={innerBoxProps}>
                    <Box
                      sx={{
                        padding: belowMd ? '15px' : '20px',
                        marginTop: belowMd ? '10px' : '20px',
                      }}
                    >
                      <Typography
                        fontSize={belowMd ? '18px' : '28px'}
                        fontWeight={700}
                      >
                        12 months
                      </Typography>
                      <Typography sx={{ marginTop: belowMd ? '20px' : '40px' }}>
                        <span
                          style={{
                            fontSize: belowMd ? '17px' : '27px',
                            fontWeight: 700,
                          }}
                        >
                          {currency}
                        </span>
                        <span
                          style={{
                            fontSize: belowMd ? '20px' : '40px',
                            fontWeight: 700,
                          }}
                        >
                          {(yearlyPrice.unitAmount / 1200).toFixed()}
                        </span>
                        <span
                          style={{
                            fontSize: belowMd ? '10px' : '20px',
                            fontWeight: 600,
                          }}
                        >
                          /Month
                        </span>
                      </Typography>
                      <Typography
                        fontSize={belowMd ? '10px' : '16px'}
                        marginBottom={belowMd ? '15px' : '30px'}
                        fontWeight={500}
                      >
                        Billed at {currency} {yearlyPrice.unitAmount / 100}/Year
                      </Typography>
                      <Button
                        {...yearButtonProps}
                        onClick={() => handleButtonClick(yearlyPrice.id || '')}
                      >
                        Enrol Now
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            {/* {quarterlyPrice && (
              <Box sx={outerBoxProps}>
                <Box sx={innerBoxProps}>
                  <Box sx={{ padding: belowMd ? '15px' : '20px' }}>
                    <Typography
                      fontSize={belowMd ? '18px' : '24px'}
                      fontWeight={700}
                    >
                      3 months
                    </Typography>
                    <Typography sx={{ marginTop: belowMd ? '15px' : '30px' }}>
                      <span
                        style={{
                          fontSize: belowMd ? '17px' : '23px',
                          fontWeight: 700,
                        }}
                      >
                        {currency}
                      </span>
                      <span
                        style={{
                          fontSize: belowMd ? '20px' : '30px',
                          fontWeight: 700,
                        }}
                      >
                        {(quarterlyPrice.unitAmount / 300).toFixed()}
                      </span>
                      <span
                        style={{
                          fontSize: belowMd ? '10px' : '16px',
                          fontWeight: 600,
                        }}
                      >
                        /Month
                      </span>
                    </Typography>
                    <Typography
                      fontSize={belowMd ? '10px' : '12px'}
                      marginBottom="25px"
                      fontWeight={500}
                    >
                      Billed at {currency} {quarterlyPrice.unitAmount / 100}
                      /Quarter
                    </Typography>
                    <Button
                      {...quaterButtonProps}
                      onClick={() => handleButtonClick(quarterlyPrice.id || '')}
                    >
                      Enrol Now
                    </Button>
                  </Box>
                </Box>
              </Box>
            )} */}
            {monthlyPrice && (
              <Box sx={outerBoxProps}>
                <Box sx={innerBoxProps}>
                  <Box sx={{ padding: belowMd ? '15px' : '20px' }}>
                    <Typography
                      fontSize={belowMd ? '18px' : '24px'}
                      fontWeight={700}
                    >
                      1 month
                    </Typography>
                    <Typography sx={{ marginTop: belowMd ? '15px' : '30px' }}>
                      <span
                        style={{
                          fontSize: belowMd ? '17px' : '23px',
                          fontWeight: 700,
                        }}
                      >
                        {currency}
                      </span>
                      <span
                        style={{
                          fontSize: belowMd ? '20px' : '30px',
                          fontWeight: 700,
                        }}
                      >
                        {(monthlyPrice.unitAmount / 100).toFixed()}
                      </span>
                      <span
                        style={{
                          fontSize: belowMd ? '10px' : '16px',
                          fontWeight: 600,
                        }}
                      >
                        /Month
                      </span>
                    </Typography>
                    <Typography
                      fontSize={belowMd ? '10px' : '12px'}
                      marginBottom="25px"
                      fontWeight={500}
                    >
                      Billed at {currency} {monthlyPrice.unitAmount / 100}
                      /Month
                    </Typography>
                    <Button
                      {...quaterButtonProps}
                      onClick={() => handleButtonClick(monthlyPrice.id || '')}
                    >
                      Enrol Now
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      {/* {isDiscountEligible && (
        <Grid item xs={12} sx={{ marginTop: '15px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '16px 13px',
              margin: { xs: '0', sm: '16px' },
              borderRadius: '7px',
              background: '#BEA4F9',
              border: '1px solid #1E293B',
            }}
          >
            <Typography fontSize="14px" fontWeight="600">
              🎉 15% Off&nbsp;
            </Typography>
            <Typography fontSize="14px">
              Premium! Use code Disc15 on the payment page.
            </Typography>
          </Box>
        </Grid>
      )} */}
    </Stack>
  );
};

export default PremiumView;
